import React from 'react';
import {
    Paper,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';

const equipmentOptionsMap: Record<string, string> = {
    'Balança': 'Balança',
    'Câmaras frias': 'Câmaras frias',
    'Banheiros': 'Banheiros',
    'Pias': 'Pias',
    'Bancas de exposição': 'Bancas de exposição',
    'Outro': 'Outro',
};

const frequencyOptionsMap: Record<string, string> = {
    'Diária': 'Diária',
    'Semanal': 'Semanal',
    'Quinzenal': 'Quinzenal',
    'Mensal': 'Mensal',
    'Outro': 'Outro',
};

const publicOptionsMap: Record<string, string> = {
    'Consumidores locais': 'Consumidores locais',
    'Turistas': 'Turistas',
    'Comerciantes': 'Comerciantes',
    'Instituições públicas': 'Instituições públicas',
};

const formatCurrency = (value: number) =>
    typeof value === 'number'
        ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : 'Não informado';

interface NonPermanentMarket {
    name: string;
    address: string;
    neighborhood: string;
    zone: string;
    frequency: string;
    otherFrequency: string;
    predominantPublic: string;
    numFamilyFarmProducers: number;
    numWomenFamilyFarmProducers: number;
    numFamilyFarmResellers: number;
    numWomenFamilyFarmResellers: number;
    numHandicraftSellers: number;
    numWomenHandicraftSellers: number;
    numIndustrialSellers: number;
    totalMarketValue: number;
    annualMarketValue: number;
    totalFamilyFarmingValue: number;
    equipments: string[];
    otherEquipment: string;
}

interface Step4SimViewProps {
    data: {
        referenceYear: number;
        nonPermanentMarkets: NonPermanentMarket[];
    };
}

const Step4SimView: React.FC<Step4SimViewProps> = ({ data }) => (
    <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
        <Typography variant="h5" color="primary" gutterBottom>
            Feiras Não Permanentes
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Ano de referência do Questionário: <b>{data.referenceYear}</b>
        </Typography>

        {(!data.nonPermanentMarkets || data.nonPermanentMarkets.length === 0) ? (
            <Typography color="text.secondary">Nenhuma feira cadastrada.</Typography>
        ) : (
            <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Nome</strong></TableCell>
                            <TableCell><strong>Bairro</strong></TableCell>
                            <TableCell><strong>Zona</strong></TableCell>
                            <TableCell><strong>Endereço</strong></TableCell>
                            <TableCell><strong>Equipamentos</strong></TableCell>
                            <TableCell><strong>Frequência</strong></TableCell>
                            <TableCell><strong>Público predominante</strong></TableCell>
                            <TableCell align="center"><strong>Produtores AF</strong></TableCell>
                            <TableCell align="center"><strong>Mulheres Produtoras AF</strong></TableCell>
                            <TableCell align="center"><strong>Revendedores AF</strong></TableCell>
                            <TableCell align="center"><strong>Mulheres Revendedoras AF</strong></TableCell>
                            <TableCell align="center"><strong>Com. Artesanato</strong></TableCell>
                            <TableCell align="center"><strong>Mulheres Artesanato</strong></TableCell>
                            <TableCell align="center"><strong>Com. Ind./Beneficiados</strong></TableCell>
                            <TableCell align="center"><strong>Valor anual (R$)</strong></TableCell>
                            <TableCell align="center"><strong>Valor AF (R$)</strong></TableCell>
                            <TableCell align="center"><strong>Valor total feira (R$)</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.nonPermanentMarkets.map((market, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{market.name || 'Não informado'}</TableCell>
                                <TableCell>{market.neighborhood || 'Não informado'}</TableCell>
                                <TableCell>{market.zone || 'Não informado'}</TableCell>
                                <TableCell>{market.address || 'Não informado'}</TableCell>
                                <TableCell>
                                    {market.equipments && market.equipments.length > 0 ? (
                                        <>
                                            {market.equipments.map((eq, i) => (
                                                <Chip
                                                    key={i}
                                                    label={equipmentOptionsMap[eq] || eq}
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            ))}
                                            {market.equipments.includes('Outro') && market.otherEquipment && (
                                                <Chip
                                                    label={market.otherEquipment}
                                                    color="secondary"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Não informado
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {market.frequency ? (
                                        <>
                                            <Chip
                                                label={frequencyOptionsMap[market.frequency] || market.frequency}
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                            {market.frequency === 'Outro' && market.otherFrequency && (
                                                <Chip
                                                    label={market.otherFrequency}
                                                    color="secondary"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Não informado
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {publicOptionsMap[market.predominantPublic] || market.predominantPublic || 'Não informado'}
                                </TableCell>
                                <TableCell align="center">{market.numFamilyFarmProducers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numWomenFamilyFarmProducers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numFamilyFarmResellers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numWomenFamilyFarmResellers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numHandicraftSellers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numWomenHandicraftSellers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{market.numIndustrialSellers ?? 'Não informado'}</TableCell>
                                <TableCell align="center">{formatCurrency(market.annualMarketValue)}</TableCell>
                                <TableCell align="center">{formatCurrency(market.totalFamilyFarmingValue)}</TableCell>
                                <TableCell align="center">{formatCurrency(market.totalMarketValue)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </Paper>
);

export default Step4SimView;